<script setup>
import TicketCard from '@/Pages/Dashboard/Partner/Support/Partials/TicketCard.vue';
import TicketReplyCard from '@/Pages/PartnerSupport/Tickets/Partials/TicketReplyCard.vue';
import FormInput from '@/components/Shared/Form/Input/Index.vue';
import {useForm} from '@inertiajs/vue3';
import CreateTimeRegistrationModal from '@/Pages/PartnerSupport/Tickets/Partials/CreateTimeRegistrationModal.vue';
import {onMounted, ref, watch} from 'vue';
import Expander from "@/components/Dashboard/Expander.vue";
import Modal from "@/components/Shared/Modal.vue";
import Swal from "sweetalert2";

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  statusOptions: {
    type: Array,
    required: true,
  },
  classificationOptions: {
    type: Array,
    required: true,
  },
  partnerSupports: {
    type: Array,
    required: true,
  },
});

const replies = ref(props.ticket.replies)
const repliesLength = ref(replies.value.length)
const replyIds = ref([]);
const showClassificationSelectorModal = ref(false)
const isShowTimeRegistrationForm = ref(false);
const replyRef = ref({});
const openHistoryExpander = ref(false);

const form = useForm({
  assignee: props.ticket.assigneeValue,
  status: props.ticket.statusValue,
  classification: props.ticket.classificationValue,
});

watch(replies, function (newReplies){
  repliesLength.value = newReplies.length
})

const saveForm = () => {
  form
    .transform(data => ({
      status: data.status?.id,
      assignee: data.assignee?.id,
      classification: data.classification?.id,
    }))
    .patch(route('partner-support.tickets.update', {ticket: props.ticket.id}), {
      preserveScroll: true,
      preserveState: true
    });
  handleCloseClassificationSelectorModal()

  // Make sure the classification changed before showing the update popup
  if (props.ticket.classificationValue?.name !== form.classification?.name) {
    Swal.fire({
      text: 'Classification updated',
      icon: 'success',
      showConfirmButton: false,
      timer: 2000,
    });
  }
};

const handleStatusUpdate = () => {
  // Only show the classification popup when the status is updated to Resolved (6)
  // and a classification was not set yet on the ticket
  if (form.status?.id === '6' && props.ticket.classification === undefined) {
    handleOpenClassificationSelectorModal()
  }else{
    saveForm()
  }
}

const handleOpenClassificationSelectorModal = () => {
  showClassificationSelectorModal.value = true;
}

const handleCloseClassificationSelectorModal = () => {
  showClassificationSelectorModal.value = false;
}

const checkHashPresent = () => {
  openHistoryExpander.value = window.location.hash !== '';
}

const updateOpenHistoryExpander = (state) => {
  openHistoryExpander.value = state;
}

const openHistoryExpanderAndScrollToHash = () => {
  // Wait 100 milliseconds to make sure everything is rendered
  checkHashPresent();
  setTimeout(() => {
    scrollTo();
  }, 100)
}

const scrollTo = () => {
  if (window.location.hash) {
    // example: #50
    let key = window.location.hash.substring(1);
    // example: 50
    let element = replyRef.value[key];
    element?.$el.scrollIntoView({behavior: 'smooth', block: 'center'});

    detectScrollingIsDoneAndStartCardAnimation(element);
  }
}

const detectScrollingIsDoneAndStartCardAnimation = (element) => {
  let scrollTimeout;
  const controller = new AbortController();
  const signal = controller.signal;

  window.addEventListener('scroll', function () {
    clearTimeout(scrollTimeout);
    // Check every 0.1s if the page is still scrolling
    scrollTimeout = setTimeout(function () {
      // When we get here the page is done scrolling
      // This stops the listener, Otherwise this code will play on every scroll
      controller.abort();
      // Set the grep attention class to start the animation
      element?.$el.classList.add("grepAttentionCard");
      setTimeout(() => {
        // When the animation is done, remove the class. So it can be called again if needed
        element?.$el.classList.remove("grepAttentionCard");
      }, 1100)
    }, 100);
  }, { signal });
}

function reloadReplies() {
  axios
    .get(route('partner-support.tickets.get-replies', props.ticket.id))
    .then(response => {
      replies.value = response.data;
    })
}
function prepareListOfReplies() {
  replyIds.value = replies.value.map(reply => reply.id);
  replyIds.value.push(0);
}

onMounted(() => {
  prepareListOfReplies()
  window.addEventListener('hashchange', () => {
    openHistoryExpanderAndScrollToHash()
  })
  openHistoryExpanderAndScrollToHash()
})

</script>

<script>
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import SupportTicketLayout from '@/Layouts/SupportTicketLayout.vue';

export default {
  layout: [DashboardLayout, SupportTicketLayout],
};
</script>

<template>
  <div>
    <div>
      <div class="alert alert-warning" role="alert">{{
          trans("support.This page has been updated. The newest message will now be displayed at the top. Below that the history can be found (click to open and close). The history is now displayed from newest to oldest. Followed by the reaction block.")
        }}
      </div>
      <h2 class="h2 mx-2">{{ ticket.subject }}</h2>
    </div>
    <div>
      <!--    Show the original message    -->
      <ticket-card
        v-if="repliesLength === 0"
        :id="0"
        :date-created="ticket.dateCreated"
        :message="ticket.message"
        :user="ticket.user"
        :is-original-message="true"
        :ref="(el) => (replyRef[0] = el)"
      />
      <!--    Show only the latest reply here    -->
      <ticket-card
        v-for="reply in replies.slice(0, 1)"
        v-bind="{ ...reply }"
        :ticket_id="props.ticket.id"
        :is-edit-able="true"
        :ref="(el) => (replyRef[reply.id] = el)"
        :available-reply-ids="replyIds"
      />
      <!--    All the other reactions need to be shown in the history expander    -->
      <expander
        v-if="repliesLength > 0"
        :title="trans('History')"
        :class="`my-6`"
        :open="openHistoryExpander"
        @opened="updateOpenHistoryExpander(true)"
        @closed="updateOpenHistoryExpander(false)"
        animation="none"
      >
        <template #content>
          <ticket-card
            v-for="reply in replies.slice(1)"
            v-bind="{ ...reply }"
            :ticket_id="props.ticket.id"
            :ref="(el) => (replyRef[reply.id] = el)"
          />
          <!--    Show the original message    -->
          <ticket-card
            v-bind="{ ...ticket }"
            :id="0"
            :is-original-message="true"
            :ref="(el) => (replyRef[0] = el)"
          />
        </template>
      </expander>

      <!--    Show the reply block    -->
      <ticket-reply-card
        :ticket="ticket"
        :type="`admin`"
        @reload-replies="reloadReplies()"
        :available-reply-ids="replyIds"
      >
        <template #footer="{ submit, replyForm }">
          <div class="d-flex justify-content-end gap">
            <form-input
              id="assignee"
              v-model="form.assignee"
              :error="form.errors['assignee.id']"
              :label-text="trans('__JSON__.Assignee')"
              :options="partnerSupports"
              :placeholder="trans('__JSON__.Select assignee')"
              label="name"
              track-by="id"
              type="select"
              @update:modelValue="saveForm"
            />

            <form-input
              id="status"
              v-model="form.status"
              :error="form.errors['status.id']"
              :label-text="trans('__JSON__.Status')"
              :options="statusOptions"
              :placeholder="trans('__JSON__.Select Status')"
              label="name"
              track-by="id"
              type="select"
              @update:modelValue="handleStatusUpdate"
            />

            <button class="button" style="margin: auto 0" @click="isShowTimeRegistrationForm = true">
              {{ trans('__JSON__.Add billable time') }}
            </button>

            <button v-if="!replyForm.private" class="button" style="margin: auto 0" @click="submit">
              {{ trans('__JSON__.Reply') }}
            </button>
            <button v-else class="button" style="margin: auto 0" @click="submit">
              {{ trans('__JSON__.Make note') }}
            </button>
          </div>
        </template>
      </ticket-reply-card>

      <create-time-registration-modal
        :show="isShowTimeRegistrationForm"
        :ticket="ticket"
        @close="isShowTimeRegistrationForm = false"
      />
    </div>
    <modal
      :show="showClassificationSelectorModal"
      :closeable="false"
    >
      <template #title>{{ trans('__JSON__.Select a Classification') }}</template>
      <template #content>
        <form-input
          id="classification"
          v-model="form.classification"
          :options="classificationOptions"
          :placeholder="trans('__JSON__.Select Classification')"
          label="name"
          track-by="id"
          type="select"
          @update:modelValue="saveForm()"
        />
      </template>
    </modal>
  </div>
</template>

<style>
.form_knob_min_width label {
  min-width: 0 !important;
}

.Expander .tile {
  background-color: transparent !important;
  border-radius: 8px;
}

.Expander__trigger {
  background-color: white !important;
  border-radius: 8px;
}

.Expander__trigger.active {
  border-radius: 8px 8px 0 0 !important;
}

.grepAttentionCard {
  animation: grepAttentionCard 1s;
}

@keyframes grepAttentionCard {
  0%, 50%, 100% {
    transform: scale(1);
  }
  25%, 75% {
    transform: scale(1.05);
  }
}
</style>
